<script lang="ts" setup>
const props = defineProps({
  max: {
    default: Number.POSITIVE_INFINITY,
    type: Number,
  },
  similarAttributes: {
    default: () => [],
    type: Array as PropType<CompanySimilarAttribute[]>,
  },
  size: {
    default: 'sm',
    type: String as PropType<UBadgeSize | UButtonSize>,
  },
  wrap: {
    default: true,
    type: Boolean,
  },
})

const { getCompaniesSearchUrl } = useSegmentation()
const { t } = useI18n()

const badges = computed((): UBadge[] => {
  return props.similarAttributes.map((similarAttribute) => {
    const similarAttributeName = similarAttribute.attribute as SegmentationAttribute
    const similarAttributeInfo = getInfoForSimilarAttribute(similarAttribute.attribute, similarAttribute.value)

    return {
      color: similarAttributeInfo?.color,
      icon: getIconForAttribute(similarAttributeName),
      label: similarAttributeInfo?.label || changeCase.noCase(similarAttribute.value),
      to: getCompaniesSearchUrl({
        attribute: similarAttributeName,
        value: similarAttribute.value,
      }),
      tooltip: similarAttributeInfo?.tooltip || '',
      value: similarAttribute.value,
    }
  })
    .filter((_similarAttribute, similarAttributeIndex) => {
      return similarAttributeIndex < props.max
    })
})

function getInfoForSimilarAttribute(attribute: string, value: string): null | UBadge {
  switch (attribute) {
    case 'ai.search':
      return {
        color: 'blue',
        label: `${value}`,
        tooltip: t('datapoints.attributes.meta-similarity'),
      }

    case 'businessType':
      return {
        color: 'green',
        label: t(`ranges.businessType.${value}`),
        tooltip: t('datapoints.attributes.about-businessType'),
      }

    case 'city.code':
    case 'continent.code':
    case 'country.code':
    case 'county.code':
    case 'state.code':
      return {
        color: 'pink',
        tooltip: t(`datapoints.attributes.locations-headquarters-${attribute.replace('.code', '')}`),
      }

    case 'industries':
      return {
        color: 'indigo',
        tooltip: t('datapoints.attributes.about-industries'),
      }

    case 'monthlyVisitors':
      return {
        color: 'blue',
        label: t(`ranges.monthlyVisitors.${value}`),
        tooltip: t('datapoints.attributes.analytics-monthlyVisitors'),
      }

    case 'revenue':
      return {
        color: 'purple',
        label: t(`ranges.revenue.${value}`),
        tooltip: t('datapoints.attributes.finances-revenue'),
      }

    case 'totalEmployees':
      return {
        color: 'violet',
        label: t(`ranges.totalEmployees.${value}`),
        tooltip: t('datapoints.attributes.about-totalEmployees'),
      }

    case 'yearFounded':
      return {
        color: 'rose',
        tooltip: t('datapoints.attributes.about-yearFounded'),
      }

    default:
      return null
  }
}
</script>

<template>
  <BaseBadges
    :badges="badges"
    clickable
    :max="max"
    :size="size"
    variant="subtle"
    :wrap="wrap"
  />
</template>
